import { shade, tint } from 'polished'
import { rawColors } from '../assets/colorsVariables'

type Color = {
  name: string
  value: string
}

type ColorName = (typeof rawColors)[number]['values'][number]['color'][number]['name']

type ColorsObject = Record<ColorName, string>

const colorsReducer = (acc: ColorsObject, color: Color): ColorsObject => {
  acc[color.name as ColorName] = color.value
  return acc
}

const generateColorsObject = (colors: typeof rawColors): ColorsObject => {
  return colors.reduce((acc, colorGroup) => {
    colorGroup.values.forEach(value => {
      value.color.reduce(colorsReducer, acc)
    })
    return acc
  }, {} as ColorsObject)
}

type PrimaryColors =
  | 'Colors/Base/Primary/1'
  | 'Colors/Base/Primary/2'
  | 'Colors/Base/Primary/3'
  | 'Colors/Base/Primary/4'
  | 'Colors/Base/Primary/5'
  | 'Colors/Base/Primary/6'
  | 'Colors/Base/Primary/7'
  | 'Colors/Base/Primary/8'
  | 'Colors/Base/Primary/9'
  | 'Colors/Base/Primary/10'

type DarkColors =
  | 'Colors/Brand/Dark/colorDark'
  | 'Colors/Brand/Dark/colorDarkBg'
  | 'Colors/Brand/Dark/colorDarkBorder'

export const getColors = (
  colorPrimary?: string,
  colorDark?: string
): ColorsObject & Record<PrimaryColors, string> & Record<DarkColors, string> => {
  const colors = generateColorsObject(rawColors)

  const blue = {
    blue1: colors['Colors/Base/Blue/1'],
    blue2: colors['Colors/Base/Blue/2'],
    blue3: colors['Colors/Base/Blue/3'],
    blue4: colors['Colors/Base/Blue/4'],
    blue5: colors['Colors/Base/Blue/5'],
    blue6: colors['Colors/Base/Blue/6'],
    blue7: colors['Colors/Base/Blue/7'],
    blue8: colors['Colors/Base/Blue/8'],
    blue9: colors['Colors/Base/Blue/9'],
    blue10: colors['Colors/Base/Blue/10'],
  }
  const customPrimary = {
    primary1: colorPrimary ? tint(0.9, colorPrimary) : blue.blue1,
    primary2: colorPrimary ? tint(0.72, colorPrimary) : blue.blue2,
    primary3: colorPrimary ? tint(0.44, colorPrimary) : blue.blue3,
    primary4: colorPrimary ? tint(0.28, colorPrimary) : blue.blue4,
    primary5: colorPrimary ? colorPrimary : blue.blue5,
    primary6: colorPrimary ? shade(0.12, colorPrimary) : blue.blue6,
    primary7: colorPrimary ? shade(0.2, colorPrimary) : blue.blue7,
    primary8: colorPrimary ? shade(0.3, colorPrimary) : blue.blue8,
    primary9: colorPrimary ? shade(0.42, colorPrimary) : blue.blue9,
    primary10: colorPrimary ? shade(0.6, colorPrimary) : blue.blue10,
  }
  // Institution dark ramp, tinted off the institution's dark color or the neutral base.
  const darkBase = colorDark ?? colors['Colors/Neutral/colorTextBase']
  const customDark = {
    colorDark: darkBase,
    colorDarkBg: tint(0.96, darkBase),
    colorDarkBorder: tint(0.9, darkBase),
  }

  return {
    ...colors,
    'Colors/Base/Primary/1': customPrimary.primary1,
    'Colors/Base/Primary/2': customPrimary.primary2,
    'Colors/Base/Primary/3': customPrimary.primary3,
    'Colors/Base/Primary/4': customPrimary.primary4,
    'Colors/Base/Primary/5': customPrimary.primary5,
    'Colors/Base/Primary/6': customPrimary.primary6,
    'Colors/Base/Primary/7': customPrimary.primary7,
    'Colors/Base/Primary/8': customPrimary.primary8,
    'Colors/Base/Primary/9': customPrimary.primary9,
    'Colors/Base/Primary/10': customPrimary.primary10,
    'Colors/Brand/Primary/colorPrimary': customPrimary.primary5,
    'Colors/Brand/Primary/colorPrimaryActive': customPrimary.primary7,
    'Colors/Brand/Primary/colorPrimaryBg': customPrimary.primary1,
    'Colors/Brand/Primary/colorPrimaryBgHover': customPrimary.primary2,
    'Colors/Brand/Primary/colorPrimaryBorder': customPrimary.primary3,
    'Colors/Brand/Primary/colorPrimaryBorderHover': customPrimary.primary4,
    'Colors/Brand/Primary/colorPrimaryHover': customPrimary.primary6,
    'Colors/Brand/Primary/colorPrimaryText': customPrimary.primary5,
    'Colors/Brand/Primary/colorPrimaryTextActive': customPrimary.primary7,
    'Colors/Brand/Primary/colorPrimaryTextHover': customPrimary.primary6,
    'Colors/Brand/Dark/colorDark': customDark.colorDark,
    'Colors/Brand/Dark/colorDarkBg': customDark.colorDarkBg,
    'Colors/Brand/Dark/colorDarkBorder': customDark.colorDarkBorder,
  }
}
